import {
  tail,
  min,
  max,
  reverse,
  range,
  isEmpty,
  pipe,
  values,
  sortWith,
  prop,
  descend,
  ascend,
  map
} from "ramda";
import { scaleLinear } from "d3-scale";
import numeral from "numeral";

import { colors } from "../consts";
import affordableHousingSupply from "../data/affordable-housing-supply.json"; // percentage
import affordableRent from "../data/affordable-rent.json"; // currency
import fairMarketRent from "../data/fair-market-rent.json"; // currency, lowerIsBetter
import housingCostBurden from "../data/housing-cost-burden.json"; // percentage, lowerIsBetter
import povertyRate from "../data/poverty-rate.json"; // percentage, lowerIsBetter
import renterHouseholds from "../data/renter-households.json"; // percentage, lowerIsBetter
import workHoursNeeded from "../data/work-hours-needed.json"; // number, lowerIsBetter

const formats = {
  percentage: "0.0%",
  currency: "$0,0",
  number: "0,0"
};

const colorRange = [
  colors.grad1,
  colors.grad2,
  colors.grad3,
  colors.grad4,
  colors.grad5
];

// this function returns:
// {
//   [dataKey]: {
//     counties: {
//       [county name]: {
//         name: string,
//         value: number,
//         valueString: string,
//         lci: number,
//         uci: number
//       }
//     },
//     average: number,
//     averageRatio: number, // 0-1
//     averageString: string,
//     min: number,
//     max: number
//   },
//   colorScale: function, // func that accepts value and returns color
//   lowerIsBetter: boolean
// }
function processData(data, formatType, lowerIsBetter = false, baseWidth) {
  const nAverage = numeral(data[0].value);
  const counties = tail(data);
  const result = counties.reduce(
    (accum, county) => {
      const nValue = numeral(county.value);
      const value = nValue.value();
      accum.counties[county.county] = {
        name: county.county,
        value,
        valueString: isEmpty(county.value)
          ? "n/a"
          : nValue.format(formats[formatType]),
        lci: county.lci ? numeral(county.lci).value() : undefined,
        uci: county.uci ? numeral(county.uci).value() : undefined
      };
      if (value) {
        accum.min = min(accum.min, value);
        accum.max = max(accum.max, value);
      }
      return accum;
    },
    {
      counties: {},
      average: nAverage.value(),
      averageString: nAverage.format(formats[formatType]),
      min: 99999,
      max: -99999
    }
  );

  const relativeMax = result.max - result.min;
  result.averageRatio =
    baseWidth +
    ((result.average - result.min) / relativeMax) * (100 - baseWidth);

  result.minString = numeral(result.min).format(formats[formatType]);
  result.maxString = numeral(result.max).format(formats[formatType]);

  const numSteps = colorRange.length - 1;
  const step = (result.max - result.min) / numSteps;
  result.colorScale = scaleLinear()
    .domain([result.min, ...range(1, numSteps).map(n => result.min + step * n)])
    .range(lowerIsBetter ? reverse(colorRange) : colorRange);

  result.lowerIsBetter = !!lowerIsBetter;

  result.countyList = pipe(
    values,
    sortWith([
      lowerIsBetter ? ascend(prop("value")) : descend(prop("value")),
      ascend(prop("name"))
    ]),
    map(county => {
      let width = 0;
      if (!isEmpty(county.value)) {
        const relativeValue = county.value - result.min;
        width = relativeValue / relativeMax;
      }
      width = `${baseWidth + width * (100 - baseWidth)}%`;

      const backgroundColor = county.value
        ? result.colorScale(county.value)
        : colors.empty;

      return {
        ...county,
        width,
        backgroundColor
      };
    })
  )(result.counties);

  return result;
}

export default {
  "affordable-housing-supply": processData(
    affordableHousingSupply,
    "percentage",
    false,
    10
  ),
  "affordable-rent": processData(affordableRent, "currency", false, 10),
  "fair-market-rent": processData(fairMarketRent, "currency", true, 60),
  "housing-cost-burden": processData(housingCostBurden, "percentage", true, 40),
  "poverty-rate": processData(povertyRate, "percentage", true, 10),
  "renter-households": processData(renterHouseholds, "percentage", true, 10),
  "work-hours-needed": processData(workHoursNeeded, "number", true, 60)
};



// WEBPACK FOOTER //
// ./src/data/map-data.js